import { jsPDF } from "jspdf";

const NAVY = [15, 30, 61];

// Detects the point in an AI-drafted letter where the signature should go.
// Looks for a closing salutation line (Sincerely, Regards, Best, Respectfully,
// Thank you), OR an explicit `[Signature]` / `_____` placeholder. Returns the
// character index at which to split the body. `-1` if no anchor is found.
function findSignatureAnchor(body) {
    if (!body) return -1;
    const placeholderRe = /\n\s*(?:_{3,}\s*\n\s*)?\[?\s*signature\s*\]?\s*(?:\n|$)/i;
    const pm = placeholderRe.exec(body);
    if (pm) return { start: pm.index, end: pm.index + pm[0].length };
    const closingRe = /\n(?:sincerely|regards|best regards|kind regards|best|respectfully(?: yours)?|thank you|yours truly|cordially),?\s*\n/i;
    const cm = closingRe.exec(body);
    if (cm) return { start: cm.index + cm[0].length, end: cm.index + cm[0].length };
    return -1;
}

export function generateLetterPdf({ title = "Letter", body = "", signatureDataUrl, meta = "" }) {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 56;
    const contentW = pageW - margin * 2;

    // When we have a real signature image, splice it into the body between the
    // closing salutation (e.g. "Sincerely,") and the sender's typed name/
    // address block — so the signature sits ABOVE the address, mirroring a
    // real handwritten letter.
    let beforeText = body || "";
    let afterText = "";
    if (signatureDataUrl) {
        const anchor = findSignatureAnchor(beforeText);
        if (anchor !== -1) {
            afterText = beforeText.slice(anchor.end).replace(/^\n+/, "");
            beforeText = beforeText.slice(0, anchor.start).replace(/\n+$/, "");
        } else {
            // Fall back — no anchor found, strip trailing placeholders and
            // put the signature at the end.
            beforeText = beforeText
                .replace(/_{3,}\s*\n\s*\[?\s*signature\s*\]?/gi, "")
                .replace(/\[?\s*signature\s*\]?\s*$/gi, "")
                .replace(/_{3,}\s*$/g, "")
                .replace(/\n{3,}$/g, "\n")
                .trimEnd();
        }
    }

    // Title
    doc.setTextColor(15, 30, 61);
    doc.setFont("times", "bold");
    doc.setFontSize(22);
    doc.text(title, margin, 60);

    if (meta) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(meta, margin, 76);
    }

    // Body (part 1) — everything up to the closing salutation
    doc.setTextColor(20, 30, 45);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const lineH = 15;
    const bottomLimit = pageH - 120;
    let y = 120;

    const renderChunk = (text) => {
        if (!text) return;
        const lines = doc.splitTextToSize(text, contentW);
        for (const ln of lines) {
            if (y > bottomLimit) {
                doc.addPage();
                y = 80;
            }
            doc.text(ln, margin, y);
            y += lineH;
        }
    };

    renderChunk(beforeText);

    // Signature block — placed inline between the closing salutation and the
    // sender's address block (mirrors a real letter).
    if (signatureDataUrl) {
        // We need ~110pt of vertical space: 60pt image + 15pt line + 30pt label
        if (y > pageH - 220) { doc.addPage(); y = 80; }
        // "Sincerely," (or similar) may already be printed above; add a small
        // "handwritten room" gap
        y += 6;
        try {
            doc.addImage(signatureDataUrl, "PNG", margin, y, 220, 60);
        } catch (e) { /* ignore */ }
        doc.setDrawColor(...NAVY);
        doc.setLineWidth(0.5);
        doc.line(margin, y + 60, margin + 220, y + 60);
        y += 70;
    }

    // Body (part 2) — sender's typed name/address block, printed AFTER the
    // signature image so the signature visually sits above the address.
    renderChunk(afterText);

    // Footer — page number only
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(120, 130, 145);
        doc.text(`Page ${i} of ${pages}`, margin, pageH - 30);
    }

    return doc;
}

export function downloadPdf(doc, filename) {
    doc.save(filename);
}
